import { forwardRef, useState, type HTMLAttributes } from 'react';
import { cn } from '../../../utils';
import {
  ShieldAlert,
  ShieldCheck,
  Clock,
  MessageSquare,
  FileEdit,
  CheckCircle2,
  XCircle,
  ChevronDown,
  Send,
} from 'lucide-react';

// ============================================================================
// EscalationCard - Minimalistic approval cards for governance escalations
// ============================================================================

export type EscalationType = 'action_approval' | 'content_review' | 'guidance' | 'compliance_review';
export type EscalationStatus = 'pending' | 'approved' | 'denied' | 'modified' | 'expired';
export type EscalationSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface EscalationCardProps extends HTMLAttributes<HTMLDivElement> {
  /** Type of escalation */
  type: EscalationType;
  /** Current status */
  status: EscalationStatus;
  /** Agent requesting the escalation */
  agentName: string;
  /** Description of the action being escalated */
  action: string;
  /** Reason for the escalation (also serves as question for guidance type) */
  reason?: string;
  /** Content to review (for content_review) */
  content?: string;
  /** Time remaining before expiry */
  expiresAt?: Date;
  /** Who resolved it */
  resolvedBy?: string;
  /** Resolution reason */
  resolutionReason?: string;
  /** Callback for approval */
  onApprove?: () => void;
  /** Callback for denial */
  onDeny?: () => void;
  /** Callback for guidance response */
  onGuidance?: (guidance: string) => void;
  /** Callback for content modification */
  onModify?: (content: string) => void;
  /** Whether the card is read-only */
  isReadOnly?: boolean;
  /** Whether actions are disabled (e.g., submitting) */
  isSubmitting?: boolean;
  /** Severity level for governance escalations */
  severity?: EscalationSeverity;
  /** Rule name that triggered the escalation */
  ruleName?: string;
}

// Status styling
const statusConfig: Record<EscalationStatus, { color: string; label: string; icon: typeof CheckCircle2 }> = {
  pending: { color: 'text-amber-600 dark:text-amber-400', label: 'Pending', icon: Clock },
  approved: { color: 'text-emerald-600 dark:text-emerald-400', label: 'Approved', icon: CheckCircle2 },
  denied: { color: 'text-rose-600 dark:text-rose-400', label: 'Denied', icon: XCircle },
  modified: { color: 'text-sky-600 dark:text-sky-400', label: 'Modified', icon: FileEdit },
  expired: { color: 'text-slate-500 dark:text-slate-400', label: 'Expired', icon: Clock },
};

// Type styling
const typeConfig: Record<EscalationType, { icon: typeof ShieldAlert; label: string; color: string }> = {
  action_approval: { icon: ShieldAlert, label: 'Approval Required', color: 'text-amber-500' },
  content_review: { icon: FileEdit, label: 'Content Review', color: 'text-sky-500' },
  guidance: { icon: MessageSquare, label: 'Guidance Needed', color: 'text-purple-500' },
  compliance_review: { icon: ShieldCheck, label: 'Compliance Review', color: 'text-violet-500' },
};

// Severity styling
const severityConfig: Record<EscalationSeverity, { border: string; bg: string; badge: string }> = {
  critical: {
    border: 'border-l-4 border-l-red-500',
    bg: 'bg-red-50 dark:bg-red-500/10',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  },
  high: {
    border: 'border-l-4 border-l-amber-500',
    bg: 'bg-amber-50 dark:bg-amber-500/10',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  },
  medium: {
    border: 'border-l-4 border-l-yellow-400',
    bg: 'bg-yellow-50 dark:bg-yellow-400/10',
    badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  },
  low: {
    border: 'border-l-4 border-l-slate-300',
    bg: 'bg-slate-50 dark:bg-slate-700/30',
    badge: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300',
  },
};

// Time remaining display
const formatTimeRemaining = (expiresAt: Date): string => {
  const now = new Date();
  const diff = expiresAt.getTime() - now.getTime();
  if (diff <= 0) return 'Expired';
  const minutes = Math.floor(diff / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

export const EscalationCard = forwardRef<HTMLDivElement, EscalationCardProps>(
  ({
    className,
    type,
    status,
    agentName,
    action,
    reason,
    content,
    expiresAt,
    resolvedBy,
    resolutionReason,
    onApprove,
    onDeny,
    onGuidance,
    onModify,
    isReadOnly = false,
    isSubmitting = false,
    severity,
    ruleName,
    ...props
  }, ref) => {
    const [showDetails, setShowDetails] = useState(false);
    const [guidanceInput, setGuidanceInput] = useState('');
    const [editedContent, setEditedContent] = useState(content || '');
    const [isEditing, setIsEditing] = useState(false);

    const isPending = status === 'pending';
    const canAct = isPending && !isReadOnly && !isSubmitting;
    const typeInfo = typeConfig[type];
    const statusInfo = statusConfig[status];
    const severityInfo = severity ? severityConfig[severity] : null;
    const TypeIcon = typeInfo.icon;
    const StatusIcon = statusInfo.icon;

    const handleGuidance = () => {
      if (guidanceInput.trim()) {
        onGuidance?.(guidanceInput.trim());
        setGuidanceInput('');
      }
    };

    const handleModify = () => {
      onModify?.(editedContent);
      setIsEditing(false);
    };

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-lg border max-w-sm',
          severityInfo
            ? cn(severityInfo.border, severityInfo.bg)
            : isPending
              ? 'border-amber-200 dark:border-yellow-600/50 bg-amber-50/50 dark:bg-yellow-950/20'
              : 'border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50',
          className
        )}
        {...props}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200/50 dark:border-slate-700/50">
          <TypeIcon className={cn('h-4 w-4', typeInfo.color)} />
          <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
            {typeInfo.label}
          </span>
          {severity && severityInfo && (
            <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', severityInfo.badge)}>
              {severity}
            </span>
          )}
          <div className="flex-1" />
          {expiresAt && isPending && (
            <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
              <Clock className="h-3 w-3" />
              <span className="tabular-nums">{formatTimeRemaining(expiresAt)}</span>
            </div>
          )}
          {!isPending && (
            <div className={cn('flex items-center gap-1 text-xs', statusInfo.color)}>
              <StatusIcon className="h-3 w-3" />
              <span>{statusInfo.label}</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="px-3 py-2.5 space-y-2">
          {/* Rule name (if provided) */}
          {ruleName && (
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Rule: <span className="font-mono">{ruleName}</span>
            </div>
          )}

          {/* Agent and Action */}
          <div className="text-sm">
            <span className="font-medium text-slate-900 dark:text-slate-100">{agentName}</span>
            <span className="text-slate-500 dark:text-slate-400"> wants to </span>
            <span className="text-slate-700 dark:text-slate-300">{action}</span>
          </div>

          {/* Reason (for guidance, this is the question) */}
          {reason && (
            <>
              {type === 'guidance' ? (
                <div className="text-sm text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 rounded px-2.5 py-1.5 border border-slate-200 dark:border-slate-700">
                  {reason}
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                  >
                    <span>Details</span>
                    <ChevronDown className={cn('h-3 w-3 transition-transform', showDetails && 'rotate-180')} />
                  </button>
                  {showDetails && (
                    <div className="text-xs text-slate-600 dark:text-slate-400">
                      {reason}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Content Preview (for content_review) */}
          {type === 'content_review' && content && !isEditing && (
            <div className="text-sm text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 rounded px-2.5 py-1.5 border border-slate-200 dark:border-slate-700 max-h-24 overflow-y-auto whitespace-pre-wrap">
              {content}
            </div>
          )}

          {/* Content Editor (for content_review) */}
          {type === 'content_review' && isEditing && (
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="w-full text-sm text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 rounded px-2.5 py-1.5 border border-sky-300 dark:border-sky-700 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
            />
          )}

          {/* Resolution info */}
          {!isPending && (resolvedBy || resolutionReason) && (
            <div className="text-xs text-slate-500 dark:text-slate-400 border-t border-slate-200 dark:border-slate-700 pt-2 mt-2">
              {resolvedBy && <span>Resolved by {resolvedBy}</span>}
              {resolutionReason && <p className="mt-0.5">{resolutionReason}</p>}
            </div>
          )}

          {/* Guidance input */}
          {canAct && type === 'guidance' && (
            <div className="flex items-end gap-2">
              <textarea
                value={guidanceInput}
                onChange={(e) => setGuidanceInput(e.target.value)}
                placeholder="Provide guidance..."
                className="flex-1 text-sm text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 rounded px-2.5 py-1.5 border border-slate-200 dark:border-slate-700 min-h-[48px] focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.ctrlKey) {
                    e.preventDefault();
                    handleGuidance();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleGuidance}
                disabled={!guidanceInput.trim() || isSubmitting}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Action buttons */}
        {canAct && type !== 'guidance' && (
          <div className="flex items-center gap-2 px-3 py-2 border-t border-slate-200/50 dark:border-slate-700/50">
            {type === 'content_review' && isEditing ? (
              <>
                <button
                  type="button"
                  onClick={handleModify}
                  disabled={isSubmitting}
                  className="px-3 py-1.5 rounded text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-50 transition-colors max-w-[120px]"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setEditedContent(content || '');
                  }}
                  className="px-3 py-1.5 rounded text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onApprove}
                    disabled={isSubmitting}
                    className="px-3 py-1.5 rounded text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 transition-colors max-w-[120px]"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={onDeny}
                    disabled={isSubmitting}
                    className="px-3 py-1.5 rounded text-sm font-medium text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950 transition-colors"
                  >
                    Deny
                  </button>
                </div>
                {type === 'content_review' && (
                  <>
                    <div className="flex-1" />
                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      disabled={isSubmitting}
                      className="px-3 py-1.5 rounded text-sm font-medium text-sky-600 hover:bg-sky-50 dark:text-sky-400 dark:hover:bg-sky-950 transition-colors"
                    >
                      Edit
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  }
);
EscalationCard.displayName = 'EscalationCard';
